import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  STEUERKATEGORIEN,
  erechnungBereitschaft,
  steuerschluesselAlle,
  steuerschluesselNorm,
  type Steuerschluessel,
} from '../api/produkte';
import { hausdaten as holeHausdaten, type Hausdaten } from '../api/rechnungen';
import { ApiFehler } from '../api/fehler';
import { markenklasse } from '../marken';
import { Feldraster, Feldzeile } from '../komponenten/Akte';
import { Dialog } from '../komponenten/Dialog';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Die E-Rechnung einrichten (RNG-13, RNG-14).
 *
 * Diese Maske beantwortet eine einzige Frage: **Können wir schon?** Sie steht
 * oben und kommt aus der Datenbank (`fach.erechnung_bereitschaft`), nicht aus
 * einer Notiz — die Antwort ändert sich mit den Stammdaten, und eine Notiz
 * täte das nicht.
 *
 * Darunter die zwei Stellen, an denen sich etwas ändern lässt:
 *
 *   1. DIE STEUERKATEGORIEN. Aus dem Prozentsatz ist die Kategorie **nicht**
 *      ableitbar. Null Prozent kann steuerfrei heißen (`E`), Reverse Charge
 *      (`AE`), innergemeinschaftliche Lieferung (`K`), Ausfuhr (`G`) oder
 *      nicht steuerbar (`O`) — alle fünf tragen 0,00 % und sind steuerlich
 *      verschiedene Sachverhalte. Das System rät hier nicht: Eine falsche
 *      Kategorie fällt erst beim Empfänger auf, und dort als Vorsteuerabzug,
 *      den es nicht gibt.
 *
 *   2. DIE BANKVERBINDUNG IN FELDERN. `bankverbindung` ist Fließtext für den
 *      Briefbogen; BT-84 verlangt die IBAN als eigenes Feld, weil der
 *      Empfänger sie maschinell liest und danach zahlt.
 *
 * WAS SATZ UND GELTUNGSZEITRAUM ANGEHT: Die sind hier **nicht** änderbar. Sie
 * stehen in jeder Rechnung, die je mit diesem Schlüssel entstanden ist; eine
 * nachträgliche Änderung veränderte deren Grundlage. Ein neuer Satz ist ein
 * neuer Schlüssel.
 */
export function Erechnung({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  /* Unbekannt, bis die Antwort da ist — sonst hieße ein gescheitertes Laden „bereit". */
  const [offen, setzeOffen] = useState<readonly { gegenstand: string; fehlt: string }[] | undefined>(
    undefined,
  );
  const [schluessel, setzeSchluessel] = useState<readonly Steuerschluessel[]>([]);
  const [haus, setzeHaus] = useState<Hausdaten | undefined>(undefined);
  const [bearbeitet, setzeBearbeitet] = useState<Steuerschluessel | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (): Promise<void> => {
    const [b, s, h] = await Promise.all([
      erechnungBereitschaft(),
      steuerschluesselAlle(),
      holeHausdaten(),
    ]);
    setzeOffen(b);
    setzeSchluessel(s);
    setzeHaus(h ?? undefined);
  }, []);

  useEffect(() => {
    void laden().catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
  }, [laden]);

  async function handle(was: () => Promise<void>): Promise<void> {
    setzeLaeuft(true);
    try {
      await was();
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  const brauchtGrund =
    bearbeitet !== undefined
    && STEUERKATEGORIEN.find((k) => k.code === bearbeitet.steuerkategorie)?.brauchtGrund === true;

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {/*
        SEITENTYP EINSTELLUNGEN (ÄA-41): Bereitschaft und Bankverbindung
        nebeneinander, die Steuerschlüssel als breite Tabelle darunter.
      */}
      <Einstellungsseite>
        {/* ── Können wir schon? ─────────────────────────────────────────── */}
        <Thema
          titel="Bereitschaft"
          zusatz={
            /* RNG-13 */
            offen === undefined ? undefined : offen.length === 0 ? (
              <span className={markenklasse('abschluss')}>bereit</span>
            ) : (
              <span className={markenklasse('aufmerksamkeit')}>nicht bereit</span>
            )
          }
        >
          {offen === undefined ? (
            <p className="hilfe">{fehler === undefined ? 'Wird geprüft …' : 'Nicht geprüft.'}</p>
          ) : offen.length === 0 ? (
            <p className="hinweiskarte">
              Es fehlt nichts. Rechnungen können als <strong>ZUGFeRD</strong> und als{' '}
              <strong>XRechnung</strong> ausgegeben werden.
            </p>
          ) : (
            <div className="hinweiskarte hinweiskarte--warnung">
              <p>
                <strong>{offen.length} Angabe(n) fehlen.</strong> Bis dahin lässt sich keine
                normkonforme E-Rechnung erzeugen — der Erzeuger weist sie ab, statt zu raten.
              </p>
              <div className="tabellenhuelle tabellenhuelle--telefon">
                <table className="liste__tabelle">
                  <thead>
                    <tr>
                      <th scope="col">Gegenstand</th>
                      <th scope="col">Was fehlt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offen.map((o) => (
                      <tr key={`${o.gegenstand}-${o.fehlt}`}>
                        <td>{o.gegenstand}</td>
                        <td>{o.fehlt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Thema>

        {/*
          RNG-13: Die Bankverbindung wird hier nur GEZEIGT.

          `fach.hausdaten` ist eine Zeile, und die Route ersetzt sie
          vollständig. Zwei Masken, die je einen Teil davon speichern,
          überschrieben einander — wer diese öffnet, dann die Geschäftsdaten
          ändert und speichert, und danach hier speichert, hätte die Änderung
          verloren, ohne dass es jemand merkt. Gepflegt wird sie deshalb an
          einer Stelle, und das Feldraster hier trägt keinen Stift.
        */}
        {haus !== undefined && (
          <Thema titel="Bankverbindung" zusatz="BT-84 bis BT-86">
            <Feldraster>
              <Feldzeile
                beschriftung="IBAN (BT-84)"
                wert={haus.iban}
                anzeige={
                  haus.iban ?? (
                    <span className={markenklasse('aufmerksamkeit')}>nicht hinterlegt</span>
                  )
                }
              />
              <Feldzeile beschriftung="BIC (BT-86)" wert={haus.bic} />
              <Feldzeile
                beschriftung="Kontoinhaber (BT-85)"
                wert={haus.kontoinhaber ?? haus.firmierung}
              />
            </Feldraster>

            <p className="feld__hilfe">
              Ohne IBAN weist die XRechnung jede Überweisungsrechnung ab (BR-DE-19). Zu ändern
              ist sie unter <strong>Geschäftsdaten</strong>, wo der ganze Satz an einer Stelle
              gepflegt wird.
            </p>
          </Thema>
        )}

        {/* ── Steuerkategorien ──────────────────────────────────────────── */}
        <Thema titel="Steuerschlüssel" zusatz="BT-118 bis BT-121" breit>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <thead>
                <tr>
                  <th scope="col">Schlüssel</th>
                  <th scope="col">Satz</th>
                  <th scope="col">Kategorie</th>
                  <th scope="col">Grund der Befreiung</th>
                  <th scope="col">Handlung</th>
                </tr>
              </thead>
              <tbody>
                {schluessel.map((s) => (
                  <tr key={s.schluessel}>
                    <td>
                      {s.schluessel}
                      <span className="kanalliste__name"> · {s.bezeichnung}</span>
                    </td>
                    <td>{s.prozent} %</td>
                    <td>
                      {s.steuerkategorie === null ? (
                        <span className={markenklasse('aufmerksamkeit')}>nicht entschieden</span>
                      ) : (
                        <>
                          <span className={markenklasse('laufend')}>{s.steuerkategorie}</span>
                          <span className="kanalliste__name">
                            {' '}
                            ·{' '}
                            {STEUERKATEGORIEN.find((k) => k.code === s.steuerkategorie)?.bezeichnung
                              ?? 'unbekannt'}
                          </span>
                        </>
                      )}
                    </td>
                    <td>{s.befreiungsgrund ?? s.befreiungsgrundCode ?? '—'}</td>
                    <td>
                      {darfPflegen && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => setzeBearbeitet(s)}
                        >
                          Bearbeiten …
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="feld__hilfe">
            Satz und Geltungszeitraum stehen hier bewusst fest: Sie stehen in jeder Rechnung, die je
            mit diesem Schlüssel entstanden ist, und eine nachträgliche Änderung veränderte deren
            Grundlage. Ein neuer Satz ist ein neuer Schlüssel.
          </p>
        </Thema>
      </Einstellungsseite>

      {bearbeitet !== undefined && (
        <Dialog
          titel={bearbeitet.schluessel}
          unterzeile={`${bearbeitet.bezeichnung} · ${bearbeitet.prozent} %`}
          beiSchliessen={() => setzeBearbeitet(undefined)}
        >
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="sk-kategorie">
              Steuerkategorie
            </label>
            <select
              id="sk-kategorie"
              value={bearbeitet.steuerkategorie ?? ''}
              onChange={(e) =>
                setzeBearbeitet({
                  ...bearbeitet,
                  steuerkategorie: e.target.value === '' ? null : e.target.value,
                })
              }
            >
              <option value="">— nicht entschieden —</option>
              {STEUERKATEGORIEN.filter(
                // Ein Regelsatz mit 0 % ist ausgeschlossen (BR-S-5), ein
                // befreiter Satz mit mehr als 0 % ebenso. Die Datenbank weist
                // beides ab; die Liste zeigt es gar nicht erst an.
                (k) => (k.code === 'S') === (Number(bearbeitet.prozent) > 0),
              ).map((k) => (
                <option key={k.code} value={k.code}>
                  {k.code} — {k.bezeichnung}
                </option>
              ))}
            </select>
            <p className="feld__hilfe">
              Aus dem Prozentsatz ist die Kategorie nicht ableitbar. Null Prozent kann steuerfrei
              heißen, Reverse Charge, innergemeinschaftliche Lieferung, Ausfuhr oder nicht
              steuerbar — alle fünf sind steuerlich verschiedene Sachverhalte.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="sk-grund">
              Grund der Befreiung
              {brauchtGrund && (
                <span className="feld__pflicht" aria-hidden="true">
                  {' '}
                  *
                </span>
              )}
            </label>
            <textarea
              id="sk-grund"
              aria-required={brauchtGrund}
              rows={2}
              value={bearbeitet.befreiungsgrund ?? ''}
              onChange={(e) =>
                setzeBearbeitet({
                  ...bearbeitet,
                  befreiungsgrund: e.target.value === '' ? null : e.target.value,
                })
              }
            />
            <p className="feld__hilfe">
              Er steht später auf jeder Rechnung mit diesem Schlüssel (BT-120). Bei Befreiung
              verlangt die Norm ihn — ohne Grund weist der Validator den Beleg ab.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="sk-code">
              VATEX-Code
            </label>
            <input
              id="sk-code"
              type="text"
              value={bearbeitet.befreiungsgrundCode ?? ''}
              placeholder="VATEX-EU-132-1I"
              onChange={(e) =>
                setzeBearbeitet({
                  ...bearbeitet,
                  befreiungsgrundCode: e.target.value === '' ? null : e.target.value,
                })
              }
            />
          </div>

          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={() => setzeBearbeitet(undefined)}>
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft}
              onClick={() =>
                void handle(async () => {
                  await steuerschluesselNorm(bearbeitet.schluessel, {
                    steuerkategorie: bearbeitet.steuerkategorie,
                    befreiungsgrund: bearbeitet.befreiungsgrund,
                    befreiungsgrundCode: bearbeitet.befreiungsgrundCode,
                  });
                  setzeRueckmeldung(`${bearbeitet.schluessel} wurde gespeichert.`);
                  setzeBearbeitet(undefined);
                  await laden();
                })
              }
            >
              Steuerschlüssel speichern
            </button>
          </div>
        </Dialog>
      )}

    </>
  );
}
